import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Send } from "lucide-react";
import { api, getToken } from "@/lib/api";
import { fmtDate, inputCls, money, StatusPill } from "@/lib/billing";
import { Spinner } from "@/components/PortalPage";

const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };

export const MonthlyReport = () => {
  const [month, setMonth] = useState(lastMonth());
  const [rep, setRep] = useState(null);
  const [charges, setCharges] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => { setRep(null); api(`/admin/billing/report?month=${month}`).then(setRep).catch((e) => toast.error(e.message)); api("/admin/billing/late-charges").then(setCharges).catch(() => {}); };
  useEffect(load, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const pdf = async () => {
    setBusy("pdf");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/billing/report.pdf?month=${month}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    setBusy("");
    if (!r.ok) return toast.error("Couldn't build the report");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(await r.blob());
    a.download = `ALR-royalty-report-${month}.pdf`;
    a.click();
  };
  const remind = async () => {
    if (!window.confirm("Email every franchisee with invoicing on their monthly summary? Franchisees who are behind get the past-due version.")) return;
    setBusy("send");
    try { const r = await api("/admin/billing/reminders", { method: "POST", body: { month } }); toast.success(`Sent ${r.sent} emails (${r.behind} behind)`); } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const remindDue = async () => {
    if (!window.confirm("Email every franchisee with invoicing on their royalty-due summary for this month now? (This also goes out automatically 3 days before the 2nd.)")) return;
    setBusy("due");
    try { const r = await api("/admin/billing/reminders/due-now", { method: "POST" }); toast.success(`Sent ${r.sent} royalty-due emails`); } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const act = async (c, action) => { try { await api(`/admin/billing/late-charges/${c.id}/${action}`, { method: "POST" }); load(); } catch (e) { toast.error(e.message); } };
  return (
    <div className="space-y-4" data-testid="monthly-report">
      <div className="flex flex-wrap items-center gap-2">
        <input type="month" className={`${inputCls} w-44`} value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month-input" />
        <button onClick={pdf} disabled={busy === "pdf"} data-testid="report-pdf-btn" className="flex items-center gap-1.5 rounded-lg bg-[var(--accent)] px-3.5 py-2.5 text-xs font-extrabold text-black disabled:opacity-50"><Download size={14} />DOWNLOAD PDF</button>
        <button onClick={remind} disabled={busy === "send"} data-testid="report-send-reminders-btn" className="flex items-center gap-1.5 rounded-lg border border-[var(--accent)] px-3.5 py-2.5 text-xs font-extrabold text-[var(--accent)] disabled:opacity-50"><Send size={14} />SEND REMINDERS NOW</button>
        <button onClick={remindDue} disabled={busy === "due"} data-testid="report-send-due-btn" className="flex items-center gap-1.5 rounded-lg border border-[#FFB800] px-3.5 py-2.5 text-xs font-extrabold text-[#FFB800] disabled:opacity-50"><Send size={14} />SEND ROYALTY-DUE NOTICE NOW</button>
        <p className="text-[11px] text-[#888]">Automatic: royalty-due notice 3 days before the 2nd, summaries on the 1st, Gross Sales true-up on the 2nd, late charges on the 3rd.</p>
      </div>
      {!rep ? <Spinner /> : !rep.rows.length ? <p className="py-6 text-center text-sm text-[#888]" data-testid="report-empty">No activity for this month.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-xs" data-testid="report-table">
            <thead className="text-[10px] tracking-[1.5px] text-[#888]"><tr>{["FRANCHISEE", "GROSS SALES", "WAIVED", "ROYALTY DUE", "COLLECTED", "UNPAID", "OPEN LATE CHARGES", "FLAGS"].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-[#262626]">
              {rep.rows.map((r) => (
                <tr key={r.franchisee_id} data-testid={`report-row-${r.franchisee_id}`}>
                  <td className="py-2 pr-3 font-bold">{r.name}</td><td>{money(r.gross_sales, r.currency)}</td><td>{money(r.waived_receipts, r.currency)}</td><td>{money(r.royalty_due, r.currency)}</td>
                  <td>{money(r.royalty_collected)}</td><td className={r.royalty_unpaid > 0 ? "text-[#FFB800]" : ""}>{money(r.royalty_unpaid)}</td><td>{money(r.late_open)}</td>
                  <td className="space-x-1">{r.behind && <StatusPill status="needs_attention" />}{r.uninvoiced_jobs.length > 0 && <span className="text-[#FFB800]">{r.uninvoiced_jobs.length} jobs not invoiced</span>}{r.below_minimum && r.gross_sales > 0 && <span className="text-[#888]"> under $5k</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div>
        <p className="mb-2 text-xs font-bold tracking-[2px] text-[#888]">LATE CHARGES (FA 4.7(c), 4.7(d))</p>
        {!charges.length ? <p className="text-sm text-[#888]" data-testid="late-charges-empty">No late charges.</p> : (
          <div className="divide-y divide-[#262626]" data-testid="late-charges-list">
            {charges.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`late-charge-${c.id}`}>
                <span className="flex-1">{c.description} <span className="text-xs text-[#888]">· {fmtDate(c.created_at)}</span></span>
                <b>{money(c.amount)}</b><StatusPill status={c.status === "billed" ? "invoiced" : c.status} />
                {["open", "billed", "needs_attention"].includes(c.status) && <>
                  <button onClick={() => act(c, "waive")} className="text-xs font-bold text-[var(--accent)]" data-testid={`late-waive-${c.id}`}>Waive</button>
                  <button onClick={() => window.confirm("Mark this charge as paid?") && act(c, "mark-paid")} className="text-xs font-bold text-[var(--accent)]" data-testid={`late-paid-${c.id}`}>Mark paid</button>
                </>}
              </div>
            ))}
          </div>
        )}
        <p className="mt-2 text-[11px] text-[#888]">Waiving here doesn't void a QuickBooks invoice that was already sent. Void it in QuickBooks too.</p>
      </div>
    </div>
  );
};

const monthLabel = (k) => new Date(`${k}-15`).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
const Cell = ({ v }) => (v?.requests ? <span className="tabular-nums"><b>{v.requests}</b><span className="text-[#888]"> / {v.quoted} / {v.accepted}</span></span> : <span className="text-[#444]">–</span>);

export const FormReport = () => {
  const [months, setMonths] = useState(6);
  const [rep, setRep] = useState(null);
  useEffect(() => { setRep(null); api(`/admin/billing/form-report?months=${months}`).then(setRep).catch((e) => toast.error(e.message)); }, [months]);
  if (!rep) return <Spinner />;
  const ks = [...rep.months].reverse();
  const tot = rep.rows.reduce((a, r) => ({ requests: a.requests + r.total.requests, quoted: a.quoted + r.total.quoted, accepted: a.accepted + r.total.accepted }), { requests: 0, quoted: 0, accepted: 0 });
  return (
    <div className="space-y-3" data-testid="form-report">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 text-xs text-[#A0A0A0]">Quote requests from each franchisee's website form. Each cell shows <b className="text-white">requests</b> / quoted / accepted.</p>
        <select className={`${inputCls} w-40`} value={months} onChange={(e) => setMonths(+e.target.value)} data-testid="form-report-months">{[3, 6, 12, 24].map((m) => <option key={m} value={m}>Last {m} months</option>)}</select>
      </div>
      <div className="flex flex-wrap gap-3 text-sm" data-testid="form-report-totals">
        <span>Requests <b data-testid="form-report-total-requests">{tot.requests}</b></span>
        <span>Quoted <b data-testid="form-report-total-quoted">{tot.quoted}</b>{tot.requests ? ` (${Math.round((tot.quoted / tot.requests) * 100)}%)` : ""}</span>
        <span>Accepted <b data-testid="form-report-total-accepted">{tot.accepted}</b>{tot.quoted ? ` (${Math.round((tot.accepted / tot.quoted) * 100)}% of quoted)` : ""}</span>
      </div>
      {!rep.rows.length ? <p className="py-6 text-center text-sm text-[#888]" data-testid="form-report-empty">No website form requests in this period yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="form-report-table">
            <thead><tr className="text-left text-[10px] tracking-[1px] text-[#888]"><th className="py-2 pr-4">FRANCHISEE</th>{ks.map((k) => <th key={k} className="px-2 py-2 text-center">{monthLabel(k).toUpperCase()}</th>)}<th className="px-2 py-2 text-right">TOTAL</th></tr></thead>
            <tbody className="divide-y divide-[#262626]">
              {rep.rows.map((r) => (
                <tr key={r.franchisee_id} data-testid={`form-report-row-${r.franchisee_id}`}>
                  <td className="py-2 pr-4 font-bold">{r.name}</td>
                  {ks.map((k) => <td key={k} className="px-2 py-2 text-center"><Cell v={r.months[k]} /></td>)}
                  <td className="px-2 py-2 text-right"><Cell v={r.total} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
